/**
 * A venue, and what is on the line in it, in words.
 *
 * One reader for the three screens that name a room — the cards on the hub,
 * the walkout, and the aftermath — so the three cannot disagree about whose
 * room it is (gotcha 29: a second copy of a lookup is a second copy that
 * drifts). The rule itself is `venues.js`'s; this only says it.
 *
 * The holder is read off the ladder **now** rather than off the card, because a
 * card is made once and held (gotcha 45) and the room can change hands across
 * the city while it sits on the table.
 */

/** A capital at the front, for a venue name that starts a line. */
export const capital = (words) => (words ? words[0].toUpperCase() + words.slice(1) : words);

/**
 * `{ name, where, stake, holder, words }` for a card's venue, or null.
 *
 * `stake` is `take` when the opponent holds it, `defend` when the player does,
 * and null when it belongs to somebody who is not in the fight.
 */
export function roomFor(venue, { ladder, opponentId = null } = {}) {
  if (!venue) return null;
  const live = ladder?.venue?.(venue.id) ?? venue;
  const holder = live.holder ?? null;
  const holderName = live.holderName ?? venue.holderName ?? null;
  const stake = holder && holder === opponentId ? 'take' : holder && holder === ladder?.playerId ? 'defend' : null;
  const words =
    stake === 'take'
      ? `${holderName}'s room — win in it and it's yours`
      : stake === 'defend'
        ? 'your room — lose in it and it goes'
        : holderName
          ? `${holderName} holds it`
          : '';
  return Object.freeze({ id: venue.id, name: venue.name, where: venue.where ?? '', stake, holder, words });
}

/** What happened to the room a recorded fight was in, as one line. */
export function changeWords(venue) {
  if (!venue) return '';
  if (venue.changed === 'took') return capital(`${venue.name} is yours — taken off ${venue.from}`);
  if (venue.changed === 'lost') return capital(`${venue.name} goes to ${venue.to}`);
  return `at ${venue.name}`;
}

